import { CHARTER_COLORS as theme } from "../../shared/controleTheme";
import { PatientControlPointCard, PatientLaunchStatusCard, SharedPatientControlComment } from "../BisControlCards";
import { latestPatientControlDossier } from "../bisDossierData";
import { inputStyle } from "../bisLayoutStyles";
import {
    controlePatientAvatarStyle,
    controlePatientDetailHeaderStyle,
    controlePatientDetailStyle,
    controlePatientEmptyDetailStyle,
    controlePatientHeaderStyle,
    controlePatientItemWrapperStyle,
    controlePatientSidebarStyle,
    controlePatientWarningCountStyle,
    controlePatientWorkspaceStyle,
    controlePointListStyle,
    lastDossierActiveButtonStyle,
    lastDossierPointInlineStyle,
    patientValidateAllPointsButtonStyle,
    patientValidateButtonStyle,
    patientValidatedButtonStyle,
} from "../bisPatientStyles";
import { PATIENT_CONTROL_INSTRUCTIONS, PATIENT_CONTROL_POINTS, PATIENT_CONTROL_POINT_TOTAL, VARIANT } from "../bisVariant";
import { ChronicBillingPanel, PdaRemarkPanel } from "../../shared/components/PatientPanels";
import { formatPatientName, patientInitials } from "../../shared/controleFormat";
import { controlPointLabel, hasUnvalidatedPatientPoints } from "../../shared/controleVariants";
import {
    isControlePatientActive,
    isPatientControlValidated,
    normalizePatientPointState,
    patientControlsForDossier,
    sharedPatientControlComment,
} from "../../shared/patientControlRules";
import {
    controlePatientCountStyle,
    controlePatientEditButtonStyle,
    controlePatientItemStyle,
    controlePatientListStyle,
    emptyPatientTextStyle,
    lastDossierButtonStyle,
} from "../../shared/styles/patientStyles";
import PatientAutocomplete from "../../../../components/PatientAutocomplete";

const lockedStyle = (locked, dimmed = 0.55) => ({ opacity: locked ? dimmed : 1, cursor: locked ? "not-allowed" : "pointer" });

function validatedPointCount(values) {
    return PATIENT_CONTROL_POINTS.filter(([pointKey]) => normalizePatientPointState(values[pointKey]).validated).length;
}

function patientRowColors({ inactive, blocking, validated, active }) {
    if (inactive) return { name: theme.danger, detail: theme.danger };
    if (blocking) return { name: "#80620e", detail: "#80620e" };
    if (validated) return { name: "#0d704a", detail: "#0d704a" };
    return { name: active ? theme.primary : theme.ink, detail: theme.muted };
}

function patientRowStatus(patient, patientActive, blocking, validated) {
    if (patient.inactive) return " · Inactif";
    if (blocking) return " · À reprendre";
    return patientActive && validated ? " · Validé" : "";
}

// Après validation de la pré-production, un patient actif qui n'était pas entièrement validé reste « à reprendre ».
function BisPatientRow({ view, patient }) {
    const { controle, editing, isPatientFrozenAfterPreProduction, preProductionLocked, workspace } = view;
    const active = String(patient.patientId) === String(controle.selectedPatientId);
    const values = (workspace.formData.controlePatients || {})[patient.patientId] || {};
    const validated = isPatientControlValidated(values);
    const patientActive = isControlePatientActive(patient);
    const frozen = isPatientFrozenAfterPreProduction(patient.patientId);
    const blocking = patientActive && (hasUnvalidatedPatientPoints(values) || (preProductionLocked && !frozen));
    const colors = patientRowColors({ inactive: patient.inactive, blocking, validated, active });
    return (
        <div
            className="controle-patient-row"
            style={controlePatientItemWrapperStyle(active, patient.inactive, validated, blocking)}
        >
            <button
                type="button"
                aria-pressed={active}
                onClick={() => controle.setSelectedPatientId(String(patient.patientId))}
                style={controlePatientItemStyle}
            >
                <span style={controlePatientAvatarStyle(active, patient.inactive, validated, blocking)}>
                    {patientInitials(patient)}
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                    <strong style={{
                        display: "block",
                        color: colors.name,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}>
                        {formatPatientName(patient)}
                    </strong>
                    <small style={{ color: colors.detail }}>
                        {patient.chambre ? `Chambre ${patient.chambre}` : "Chambre non renseignée"}
                        {patientRowStatus(patient, patientActive, blocking, validated)}
                    </small>
                </span>
                <span style={blocking ? controlePatientWarningCountStyle : controlePatientCountStyle}>
                    {patientActive ? `${validatedPointCount(values)}/${PATIENT_CONTROL_POINT_TOTAL}` : "Inactif"}
                </span>
            </button>
            <button
                type="button"
                className="controle-patient-edit-button"
                aria-label={`Modifier ${formatPatientName(patient)}`}
                title="Modifier le patient"
                disabled={controle.actionLoading || frozen}
                onClick={() => editing.openIdentity(patient)}
                style={{ ...controlePatientEditButtonStyle, ...lockedStyle(frozen, 0.45) }}
            >
                ✎
            </button>
        </div>
    );
}

function BisPatientSidebar({ view }) {
    const { activeControlePatientCount, activeDossierEhpadId, controle, preProductionLocked, sortedControlePatients, workspace } = view;
    return (
        <aside style={controlePatientSidebarStyle}>
            <div style={controlePatientHeaderStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                    <span style={{ color: "#64748b", fontSize: "12px", fontWeight: 900, textTransform: "uppercase" }}>Patients</span>
                    <strong style={{ color: "#172033", fontSize: "13px" }}>{activeControlePatientCount}</strong>
                </div>
                <PatientAutocomplete
                    value={controle.search}
                    onChange={preProductionLocked ? () => {} : controle.setSearch}
                    onSelect={controle.selectSuggestion}
                    ehpadId={activeDossierEhpadId}
                    placeholder={preProductionLocked ? "Pré-production figée" : "Rechercher un patient"}
                    inputStyle={{
                        ...inputStyle,
                        width: "100%",
                        height: "34px",
                        background: preProductionLocked ? "#f1f5f9" : "white",
                        color: preProductionLocked ? "#64748b" : "#0f172a",
                        cursor: preProductionLocked ? "not-allowed" : "text",
                    }}
                />
            </div>
            <div style={controlePatientListStyle}>
                {controle.loading && <p style={emptyPatientTextStyle}>Chargement...</p>}
                {!controle.loading && controle.patients.length === 0 && <p style={emptyPatientTextStyle}>Aucun patient pour cet EHPAD.</p>}
                {!controle.loading && sortedControlePatients.map(patient => (
                    <BisPatientRow key={patient.patientId} view={view} patient={patient} />
                ))}
            </div>
            <span style={{ color: "#64748b", fontSize: "12px", padding: "0 14px 14px" }}>
                {workspace.activeDossier.ehpadNom || workspace.selectedEhpad?.nom || "EHPAD du dossier"}
            </span>
        </aside>
    );
}

function BisPatientActions({ view, patient, patientValues, completedCount, frozen }) {
    const { setShowLastDossierInline, showLastDossierInline, updatePatientValidation, validateAllPatientPoints } = view;
    const allPointsValidated = completedCount === PATIENT_CONTROL_POINT_TOTAL;
    const patientValidated = isPatientControlValidated(patientValues);
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button
                type="button"
                onClick={() => setShowLastDossierInline(current => !current)}
                style={showLastDossierInline ? lastDossierActiveButtonStyle : lastDossierButtonStyle}
            >
                {showLastDossierInline ? "Masquer les valeurs du dernier dossier" : "Voir les valeurs du dernier dossier"}
            </button>
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "62px",
                height: "32px",
                borderRadius: "999px",
                background: allPointsValidated ? theme.successSoft : theme.primarySoft,
                color: allPointsValidated ? "#0d704a" : theme.primary,
                fontSize: "13px",
                fontWeight: 900,
            }}>
                {completedCount}/{PATIENT_CONTROL_POINT_TOTAL}
            </span>
            <button
                type="button"
                onClick={() => validateAllPatientPoints(patient.patientId)}
                disabled={frozen}
                style={{ ...patientValidateAllPointsButtonStyle, ...lockedStyle(frozen) }}
            >
                Valider tous les points
            </button>
            <button
                type="button"
                onClick={() => updatePatientValidation(patient.patientId, !patientValidated)}
                disabled={frozen}
                style={{ ...(patientValidated ? patientValidatedButtonStyle : patientValidateButtonStyle), ...lockedStyle(frozen) }}
            >
                {patientValidated ? "Patient validé" : "Valider le patient"}
            </button>
        </div>
    );
}

function BisPatientPoints({ view, patient, patientValues, frozen }) {
    const { activeControleMode, activeDossierEhpadId, showLastDossierInline, updatePatientControl, updatePatientLaunchStatus, updatePatientSharedControlComment, workspace } = view;
    const lastDossier = showLastDossierInline
        ? latestPatientControlDossier(workspace.dossiers, workspace.activeDossier, patient.patientId, activeDossierEhpadId)
        : null;
    const lastControls = lastDossier ? patientControlsForDossier(lastDossier, patient.patientId) : null;
    return (
        <div style={controlePointListStyle}>
            {PATIENT_CONTROL_POINTS.map(([pointKey, label], index) => (
                <PatientControlPointCard
                    key={pointKey}
                    number={index + 2}
                    label={controlPointLabel(VARIANT, "avantLancement", pointKey, label, activeControleMode)}
                    instruction={PATIENT_CONTROL_INSTRUCTIONS[pointKey]}
                    value={normalizePatientPointState(patientValues[pointKey])}
                    lastDossier={lastDossier}
                    lastValue={lastDossier ? normalizePatientPointState(lastControls?.[pointKey]) : null}
                    lastComment={showLastDossierInline ? sharedPatientControlComment(lastControls) : ""}
                    onChange={patch => updatePatientControl(patient.patientId, pointKey, patch)}
                    disabled={frozen}
                />
            ))}
            {showLastDossierInline && !lastDossier && (
                <div style={lastDossierPointInlineStyle}>
                    <span style={{ color: "#1d4ed8", fontSize: "12px", fontWeight: 950 }}>
                        Dernier dossier
                    </span>
                    <span style={{ color: "#64748b", fontSize: "12px", fontWeight: 800 }}>
                        Aucun ancien dossier de lot avec contrôle patient enregistré pour ce patient.
                    </span>
                </div>
            )}
            <SharedPatientControlComment
                value={sharedPatientControlComment(patientValues)}
                onChange={comment => updatePatientSharedControlComment(patient.patientId, comment)}
                disabled={frozen}
            />
            <PatientLaunchStatusCard
                value={normalizePatientPointState(patientValues.tracageLogiciels)}
                lastDossier={lastDossier}
                lastValue={lastDossier ? normalizePatientPointState(lastControls?.tracageLogiciels) : null}
                onChange={launchStatus => updatePatientLaunchStatus(patient.patientId, launchStatus)}
                disabled={frozen}
            />
        </div>
    );
}

function BisPatientDetail({ view, patient }) {
    const { chronic, controle, editing, isPatientFrozenAfterPreProduction, workspace } = view;
    const patientActive = isControlePatientActive(patient);
    const patientValues = (workspace.formData.controlePatients || {})[patient.patientId] || {};
    const completedCount = validatedPointCount(patientValues);
    const frozen = isPatientFrozenAfterPreProduction(patient.patientId);
    return (
        <div style={controlePatientDetailStyle}>
            <div style={controlePatientDetailHeaderStyle}>
                <div>
                    <h2 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>{formatPatientName(patient)}</h2>
                    <div style={{ color: "#64748b", fontSize: "13px", marginTop: "3px" }}>
                        {patient.chambre ? `Chambre ${patient.chambre}` : "Chambre non renseignée"}
                        {patientActive ? ` · ${completedCount}/${PATIENT_CONTROL_POINT_TOTAL} points validés` : " · Patient inactif"}
                        {patientActive && isPatientControlValidated(patientValues) ? " · Patient validé" : ""}
                    </div>
                </div>
                {patientActive && (
                    <BisPatientActions view={view} patient={patient} patientValues={patientValues} completedCount={completedCount} frozen={frozen} />
                )}
            </div>
            {patientActive ? (
                <BisPatientPoints view={view} patient={patient} patientValues={patientValues} frozen={frozen} />
            ) : (
                <div style={controlePatientEmptyDetailStyle}>
                    <h2 style={{ margin: 0, fontSize: "17px", color: "#0f172a" }}>Patient inactif</h2>
                    <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: "13px" }}>Aucun contrôle patient attendu.</p>
                </div>
            )}
            <PdaRemarkPanel controle={controle} editing={editing} patient={patient} disabled={frozen} idPrefix="controle-bis" />
            <ChronicBillingPanel chronic={chronic} editing={editing} patient={patient} disabled={frozen} />
        </div>
    );
}

export function renderPatientControleTab(view) {
    const selectedPatient = view.controle.selectedPatient;
    return (
        <div style={controlePatientWorkspaceStyle}>
            <BisPatientSidebar view={view} />
            <main style={{ minWidth: 0 }}>
                {!selectedPatient ? (
                    <div style={controlePatientEmptyDetailStyle}>
                        <h2 style={{ margin: 0, fontSize: "17px", color: "#0f172a" }}>Aucun patient sélectionné</h2>
                        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: "13px" }}>Choisissez un patient dans la liste pour saisir ses contrôles.</p>
                    </div>
                ) : (
                    <BisPatientDetail view={view} patient={selectedPatient} />
                )}
            </main>
        </div>
    );
}
